// GroupsTab — the People page's Groups tab: a sectioned card grid from
// GET /group-sections. Sections are collapsible; the default section renders as
// "Unassigned". Toolbar = search (client filter) + summary + New section +
// Create group. Section ⋮ renames (PATCH /group-sections/:id) or deletes the
// section (its groups fall back to "Unassigned", they aren't deleted with it);
// card ⋮ opens the details drawer, moves the group to another section (an inline
// list, current one check-marked — its own PATCH /groups/:id/move, not a partial
// group update) or deletes it; the card checkboxes drive a selection bar (Move
// to section menu · Move to unassigned · Delete) that runs over the whole
// selection — no bulk endpoint, so the per-group calls are looped.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Avatar,
  AvatarStack,
  Button,
  Checkbox,
  Chip,
  ConfirmModal,
  Icon,
  Input,
} from '@/components/common';
import {
  useDeleteGroupMutation,
  useDeleteGroupSectionMutation,
  useGetGroupSectionsQuery,
  useMoveGroupMutation,
  type GroupItem,
  type GroupSection,
} from '@/api/endpoints/groupsApi';
import useModuleAccess from '@/hooks/useModuleAccess';
import CreateGroupModal from './CreateGroupModal';
import CreateSectionModal from './CreateSectionModal';
import GroupCardActions from './GroupCardActions';
import GroupsBulkActions from './GroupsBulkActions';
import GroupDetailsDrawer from './GroupDetailsDrawer';
import SectionRowActions from './SectionRowActions';
import initialsOf from '@/utils/initials';

/** Groups a delete runs over, plus whether it came from the selection bar (so
 *  only then does success clear the selection). */
interface ActionTarget {
  groups: GroupItem[];
  fromSelection: boolean;
}

interface GroupsTabProps {
  /** Report the total group count up so the tab badge can show it. */
  onCount?: (count: number) => void;
}

const GroupsTab = ({ onCount }: GroupsTabProps) => {
  const { t } = useTranslation();
  const { data: sections = [], isLoading, isError, isSuccess } = useGetGroupSectionsQuery();
  const [moveGroup] = useMoveGroupMutation();
  const [deleteGroup] = useDeleteGroupMutation();
  const [deleteSection, { isLoading: deletingSection }] = useDeleteGroupSectionMutation();

  // Verb gates (hidden, not disabled, when missing): sections/groups create →
  // `create`; move/rename → `edit`; group/section delete → `delete`.
  const { canCreate, canEdit, canDelete } = useModuleAccess('people');

  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sectionOpen, setSectionOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [addSectionId, setAddSectionId] = useState<string | undefined>(undefined);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<GroupSection | null>(null);
  const [sectionDeleteTarget, setSectionDeleteTarget] = useState<GroupSection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ActionTarget | null>(null);
  // Sequential loops, so the mutations' own isLoading flickers between calls.
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Resolve the open group from the (possibly refetched) list; undefined once deleted.
  const selectedGroup = useMemo(
    () => sections.flatMap((s) => s.groups).find((g) => g.id === selectedGroupId),
    [sections, selectedGroupId],
  );

  // Summary counts (unfiltered): non-default sections + all groups, per design.
  const totalGroups = useMemo(() => sections.reduce((n, s) => n + s.groups.length, 0), [sections]);
  const totalSections = useMemo(() => sections.filter((s) => !s.isDefault).length, [sections]);

  // Surface the total up to the People page so the Groups tab badge fills in
  // once this tab (and its data) has loaded.
  useEffect(() => {
    if (isSuccess) onCount?.(totalGroups);
  }, [isSuccess, totalGroups, onCount]);

  // Resolved from the unfiltered tree, so the bar keeps counting cards the
  // search has hidden — and drops ids whose group a refetch removed.
  const selectedGroups = useMemo(
    () => sections.flatMap((s) => s.groups).filter((g) => selectedIds.has(g.id)),
    [sections, selectedIds],
  );

  // The default section is the move-to-unassigned target; the named ones are the
  // Move-to menu's rows.
  const defaultSection = useMemo(() => sections.find((s) => s.isDefault), [sections]);
  const namedSections = useMemo(() => sections.filter((s) => !s.isDefault), [sections]);

  // Pointless when every selected group already sits in the default section.
  const canMoveToUnassigned = useMemo(
    () =>
      selectedGroups.some(
        (g) => !sections.find((s) => s.groups.some((x) => x.id === g.id))?.isDefault,
      ),
    [selectedGroups, sections],
  );

  // Client-side search: keep groups whose name matches; drop emptied sections.
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return sections;
    return sections
      .map((s) => ({ ...s, groups: s.groups.filter((g) => g.name.toLowerCase().includes(q)) }))
      .filter((s) => s.groups.length > 0);
  }, [sections, q]);

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openCreateGroup = (sectionId?: string) => {
    setAddSectionId(sectionId);
    setGroupOpen(true);
  };

  // ---- Actions ----

  const moveGroups = async (groups: GroupItem[], sectionId: string, fromSelection: boolean) => {
    // Nothing blocks the menu from firing twice, so guard the in-flight run.
    if (groups.length === 0 || moving) return;
    setMoving(true);
    try {
      for (const group of groups) {
        await moveGroup({ id: group.id, body: { groupSection: sectionId } }).unwrap();
      }
      toast.success(
        t(groups.length === 1 ? 'groups_moved_one' : 'groups_moved_other', {
          count: groups.length,
        }),
      );
      if (fromSelection) setSelectedIds(new Set());
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setMoving(false);
    }
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      for (const group of deleteTarget.groups) {
        await deleteGroup(group.id).unwrap();
      }
      toast.success(
        deleteTarget.groups.length === 1
          ? t('group_deleted')
          : t('groups_bulk_deleted', { count: deleteTarget.groups.length }),
      );
      if (deleteTarget.fromSelection) setSelectedIds(new Set());
      // The drawer, if it was showing one of these, has nothing left to show.
      if (deleteTarget.groups.some((g) => g.id === selectedGroupId)) setSelectedGroupId(null);
      setDeleteTarget(null);
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setDeleting(false);
    }
  };

  const runDeleteSection = async () => {
    if (!sectionDeleteTarget) return;
    try {
      await deleteSection(sectionDeleteTarget.id).unwrap();
      toast.success(t('section_deleted'));
      setSectionDeleteTarget(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const renderCard = (group: GroupItem, sectionId: string) => {
    const selected = selectedIds.has(group.id);
    return (
      <div
        key={group.id}
        className={`group-card${selected ? ' group-card--selected' : ''}`}
        role='button'
        tabIndex={0}
        onClick={() => setSelectedGroupId(group.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedGroupId(group.id);
          }
        }}
      >
        <div className='group-card__head'>
          {/* Selection + menu act independently — don't open the details drawer. */}
          <span className='group-card__select' onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onChange={() => toggleSelect(group.id)}
              aria-label={group.name}
            />
          </span>
          <span className='group-card__tile'>
            <Icon name='users-round' size={18} />
          </span>
          <span className='group-card__identity'>
            <span className='group-card__name'>{group.name}</span>
            <span className='group-card__count'>
              {t(group.membersCount === 1 ? 'groups_members_one' : 'groups_members_other', {
                count: group.membersCount,
              })}
            </span>
          </span>
          <span className='group-card__menu' onClick={(e) => e.stopPropagation()}>
            <GroupCardActions
              groupName={group.name}
              sections={sections}
              currentSectionId={sectionId}
              onOpenDetails={() => setSelectedGroupId(group.id)}
              onMoveToSection={canEdit ? (target) => moveGroups([group], target, false) : undefined}
              onDelete={
                canDelete
                  ? () => setDeleteTarget({ groups: [group], fromSelection: false })
                  : undefined
              }
            />
          </span>
        </div>

        <div className='group-card__footer'>
          {group.members.length > 0 ? (
            <AvatarStack max={5} size={28}>
              {group.members.map((m) => (
                <Avatar key={m.id} initials={initialsOf(m.fullName)} size={28} />
              ))}
            </AvatarStack>
          ) : (
            <span className='group-card__no-members'>{t('groups_no_members')}</span>
          )}
          {/* Join-by-link is the notable state, so it gets an info Chip;
              invite-only stays the quiet inline label. */}
          {group.allowJoinByLink ? (
            <Chip variant='info' iconStart='link'>
              {t('groups_join_link')}
            </Chip>
          ) : (
            <span className='group-card__access'>
              <Icon name='lock' size={14} />
              {t('groups_invite_only')}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='groups-tab'>
      <div className='groups-tab__toolbar'>
        <div className='groups-tab__toolbar-lead'>
          <Input
            size='sm'
            className='groups-tab__search'
            iconStart='search'
            placeholder={t('groups_search_ph')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className='groups-tab__summary'>
            {t('groups_summary', { groups: totalGroups, sections: totalSections })}
          </span>
        </div>
        <div className='groups-tab__toolbar-actions'>
          {canCreate && (
            <>
              <Button
                variant='secondary'
                iconStart='folder-plus'
                onClick={() => setSectionOpen(true)}
              >
                {t('groups_new_section')}
              </Button>
              <Button iconStart='plus' onClick={() => openCreateGroup(undefined)}>
                {t('groups_create')}
              </Button>
            </>
          )}
        </div>
      </div>

      {selectedGroups.length > 0 && (
        <div className='groups-tab__selection-bar'>
          <div className='groups-tab__selection-lead'>
            <span className='groups-tab__selected'>
              {t('groups_selected_count', { count: selectedGroups.length })}
            </span>
            {(canEdit || canDelete) && <span className='groups-tab__selection-divider' />}
            <GroupsBulkActions
              sections={namedSections}
              canMoveToUnassigned={canMoveToUnassigned}
              onMoveToSection={
                canEdit ? (sectionId) => moveGroups(selectedGroups, sectionId, true) : undefined
              }
              onMoveToUnassigned={
                canEdit
                  ? () => defaultSection && moveGroups(selectedGroups, defaultSection.id, true)
                  : undefined
              }
              onDelete={
                canDelete
                  ? () => setDeleteTarget({ groups: selectedGroups, fromSelection: true })
                  : undefined
              }
            />
          </div>
          <Button variant='ghost' size='sm' onClick={() => setSelectedIds(new Set())}>
            {t('groups_selection_clear')}
          </Button>
        </div>
      )}

      {isLoading ? (
        <p className='groups-tab__status'>{t('text_loading')}</p>
      ) : isError ? (
        <p className='groups-tab__status'>{t('error_generic')}</p>
      ) : filtered.length === 0 ? (
        <p className='groups-tab__status'>
          <Icon name='users-round' size={26} />
          {q ? t('groups_no_results') : t('groups_empty')}
        </p>
      ) : (
        filtered.map((section) => {
          const isCollapsed = collapsed.has(section.id);
          return (
            <section key={section.id} className='groups-tab__section'>
              <div className='groups-tab__section-head'>
                <button
                  type='button'
                  className='groups-tab__section-toggle'
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleCollapse(section.id)}
                >
                  <Icon name={isCollapsed ? 'chevron-right' : 'chevron-down'} size={18} />
                  <Icon
                    name={section.isDefault ? 'inbox' : 'folder'}
                    size={18}
                    className='groups-tab__section-icon'
                  />
                  <span className='groups-tab__section-name'>{section.name}</span>
                  <span className='groups-tab__section-count'>{section.groups.length}</span>
                </button>
                {!section.isDefault && (
                  <div className='groups-tab__section-actions'>
                    {canCreate && (
                      <Button
                        size='sm'
                        variant='ghost'
                        iconStart='plus'
                        onClick={() => openCreateGroup(section.id)}
                      >
                        {t('groups_add_group')}
                      </Button>
                    )}
                    <SectionRowActions
                      sectionName={section.name}
                      onRename={
                        canEdit
                          ? () =>
                              setRenameTarget(sections.find((s) => s.id === section.id) ?? section)
                          : undefined
                      }
                      onDelete={
                        canDelete
                          ? () =>
                              setSectionDeleteTarget(
                                sections.find((s) => s.id === section.id) ?? section,
                              )
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>

              {!isCollapsed && (
                <div className='groups-tab__grid'>
                  {section.groups.map((group) => renderCard(group, section.id))}
                </div>
              )}
            </section>
          );
        })
      )}

      <CreateSectionModal open={sectionOpen} onClose={() => setSectionOpen(false)} />
      <CreateSectionModal
        open={!!renameTarget}
        section={renameTarget}
        onClose={() => setRenameTarget(null)}
      />
      <CreateGroupModal
        open={groupOpen}
        onClose={() => setGroupOpen(false)}
        sections={sections}
        defaultSectionId={addSectionId}
      />
      <GroupDetailsDrawer
        open={!!selectedGroup}
        group={selectedGroup}
        onClose={() => setSelectedGroupId(null)}
      />

      {deleteTarget && (
        <ConfirmModal
          open
          onClose={() => setDeleteTarget(null)}
          onConfirm={runDelete}
          tone='danger'
          icon='trash-2'
          title={
            deleteTarget.groups.length === 1
              ? t('group_delete_confirm_title', { name: deleteTarget.groups[0].name })
              : t('groups_bulk_delete_title', { count: deleteTarget.groups.length })
          }
          description={
            deleteTarget.groups.length === 1
              ? t('group_delete_confirm_body')
              : t('groups_bulk_delete_body')
          }
          confirmLabel={t(
            deleteTarget.groups.length === 1
              ? 'group_delete_confirm'
              : 'groups_bulk_delete_confirm',
          )}
          confirmVariant='danger'
          loading={deleting}
        />
      )}

      {sectionDeleteTarget && (
        <ConfirmModal
          open
          onClose={() => setSectionDeleteTarget(null)}
          onConfirm={runDeleteSection}
          tone='warning'
          icon='folder-x'
          title={t('section_delete_confirm_title', { name: sectionDeleteTarget.name })}
          description={t('section_delete_confirm_body')}
          confirmLabel={t('section_delete_confirm')}
          confirmVariant='danger'
          loading={deletingSection}
        />
      )}
    </div>
  );
};

export default GroupsTab;
